import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-shadow disabled:pointer-events-none aria-disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-[var(--color-blue-600)] hover:text-white dark:hover:bg-[var(--color-blue-300)] disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted disabled:hover:text-muted-foreground aria-disabled:bg-muted aria-disabled:text-muted-foreground',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted disabled:hover:text-muted-foreground aria-disabled:bg-muted aria-disabled:text-muted-foreground',
        outline:
          'border bg-background shadow-xs hover:bg-[var(--color-blue-600)] hover:text-primary-foreground dark:bg-input/30 dark:border-input dark:hover:bg-[var(--color-blue-300)] dark:hover:text-primary-foreground disabled:border-muted disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted disabled:hover:text-muted-foreground aria-disabled:border-muted aria-disabled:bg-muted aria-disabled:text-muted-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-[var(--color-blue-600)] hover:text-primary-foreground dark:hover:bg-[var(--color-blue-300)] disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted disabled:hover:text-muted-foreground aria-disabled:bg-muted aria-disabled:text-muted-foreground',
        ghost:
          'hover:bg-[var(--color-blue-600)] hover:text-primary-foreground dark:hover:bg-[var(--color-blue-300)] disabled:bg-transparent disabled:text-muted-foreground disabled:hover:bg-transparent aria-disabled:bg-transparent aria-disabled:text-muted-foreground',
        link: 'text-primary underline-offset-4 hover:underline disabled:text-muted-foreground disabled:no-underline aria-disabled:text-muted-foreground aria-disabled:no-underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const Button = React.forwardRef<
  React.ElementRef<'button'>,
  React.ComponentPropsWithoutRef<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
})

Button.displayName = 'Button'

export { Button, buttonVariants }
